import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { usersApi, adminApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge, KpiCard } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

export function UsersPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all'|'active'|'disabled'|'locked'>('all');

  const { data, isLoading, error } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list({ pageSize: 200 }) });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: adminApi.listRoles });

  const createMut = useMutation({
    mutationFn: (p: any) => usersApi.create(p),
    onSuccess: () => { toast.success('User created'); qc.invalidateQueries({ queryKey: ['users'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => usersApi.update(id, payload),
    onSuccess: () => { toast.success('User updated'); qc.invalidateQueries({ queryKey: ['users'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => usersApi.update(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'User activated' : 'User disabled'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredUsers } = useMemo(() => {
    const users = data?.data ?? [];
    const kpis = {
      total: users.length,
      active: users.filter((u: any) => u.status === 'active').length,
      disabled: users.filter((u: any) => u.status === 'disabled').length,
      locked: users.filter((u: any) => u.status === 'locked').length,
    };
    const filtered = users.filter((u: any) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (search) { const q = search.toLowerCase(); return u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q); }
      return true;
    });
    return { kpis, filteredUsers: filtered };
  }, [data, search, statusFilter]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Administration" title="Users"
        subtitle="Manage staff access, roles, and branch permissions across your organization."
        actions={
          <>
            <button className="btn-secondary"><ImportIcon /> Import</button>
            <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New user</button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Total users" value={kpis.total} icon={<UsersIcon />} />
        <KpiCard label="Active" value={kpis.active} icon={<CheckIcon />} />
        <KpiCard label="Disabled" value={kpis.disabled} icon={<PauseIcon />} />
        <KpiCard label="Locked" value={kpis.locked} icon={<LockIcon />} />
      </div>

      <div className="card p-1.5 mb-4 flex gap-1 items-center">
        <div className="flex-1 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by name or email…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2" />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {(['all','active','disabled','locked'] as const).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs font-semibold rounded-pill transition-all ${statusFilter === s ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900'}`}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className={`flex gap-4 ${viewing ? 'items-start' : ''}`}>
        <div className={viewing ? 'flex-1 min-w-0' : 'w-full'}>
          {isLoading ? <div className="card"><LoadingState rows={6} /></div>
            : error ? <ErrorState error={error} />
            : (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th><th>Email</th><th>Status</th>
                        <th>Last login</th><th>Created</th><th className="text-right w-44">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr><td colSpan={6} className="!p-0">
                          <EmptyState
                            message={search || statusFilter !== 'all' ? 'No users match your filters' : 'No users yet'}
                            hint="Invite your first team member to get started."
                            action={!search && statusFilter === 'all' && (
                              <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New user</button>
                            )}
                          />
                        </td></tr>
                      ) : filteredUsers.map((u: any) => (
                        <tr key={u.id} className={`cursor-pointer transition-colors ${viewing === u.id ? 'bg-sage-50' : 'hover:bg-canvas-subtle/50'}`}
                          onClick={() => setViewing(viewing === u.id ? null : u.id)}>
                          <td>
                            <div className="flex items-center gap-3">
                              <Avatar name={u.full_name || u.email} />
                              <div className="min-w-0">
                                <div className="font-semibold text-ink-900 truncate">{u.full_name || '—'}</div>
                                <div className="text-xs text-ink-500 font-mono">{u.id.slice(0, 8)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-ink-700">{u.email}</td>
                          <td><StatusBadge status={u.status} /></td>
                          <td className="text-sm text-ink-600">
                            {u.last_login_at ? formatRelative(u.last_login_at) : <span className="text-ink-400 italic">Never</span>}
                          </td>
                          <td className="text-sm text-ink-600">
                            {new Date(u.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                                onClick={() => setEditing(u)}>Edit</button>
                              <button
                                className={`text-xs font-medium px-2 py-1 rounded transition-colors ${u.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                                disabled={toggleMut.isPending}
                                onClick={() => toggleMut.mutate({ id: u.id, status: u.status === 'active' ? 'disabled' : 'active' })}>
                                {u.status === 'active' ? 'Disable' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>

        {viewing && (
          <div className="w-80 flex-shrink-0">
            <UserDetailPanel
              userId={viewing}
              onClose={() => setViewing(null)}
              onEdit={(u) => setEditing(u)}
              onToggle={(u) => toggleMut.mutate({ id: u.id, status: u.status === 'active' ? 'disabled' : 'active' })}
              togglePending={toggleMut.isPending}
            />
          </div>
        )}
      </div>

      {showCreate && (
        <UserModal roles={rolesQuery.data ?? []} onSubmit={(p) => createMut.mutate(p)}
          onClose={() => setShowCreate(false)} loading={createMut.isPending} />
      )}
      {editing && (
        <UserModal roles={rolesQuery.data ?? []} initial={editing}
          onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })}
          onClose={() => setEditing(null)} loading={updateMut.isPending} />
      )}
    </div>
  );
}

// ── User detail panel ─────────────────────────────────────────
function UserDetailPanel({ userId, onClose, onEdit, onToggle, togglePending }: {
  userId: string; onClose: () => void;
  onEdit: (u: any) => void; onToggle: (u: any) => void; togglePending: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersApi.get(userId),
  });

  return (
    <div className="card overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-ink-100 flex items-start justify-between gap-2">
        <div className="eyebrow text-ink-500">User detail</div>
        <button className="w-6 h-6 flex items-center justify-center text-ink-400 hover:text-ink-900 rounded transition-colors text-sm"
          onClick={onClose} aria-label="Close">✕</button>
      </div>

      {isLoading ? (
        <div className="p-5"><LoadingState rows={4} /></div>
      ) : error ? (
        <div className="p-5"><ErrorState error={error} /></div>
      ) : data ? (
        <>
          <div className="px-5 py-4 border-b border-ink-100">
            <div className="flex items-center gap-3 mb-3">
              <Avatar name={data.full_name || data.email} size="lg" />
              <div className="min-w-0">
                <div className="font-bold text-ink-900">{data.full_name || '—'}</div>
                <div className="text-xs text-ink-500 truncate">{data.email}</div>
              </div>
            </div>
            <StatusBadge status={data.status} />
          </div>

          <div className="px-5 py-4 space-y-3 border-b border-ink-100 text-sm">
            <div>
              <div className="eyebrow text-ink-400 mb-0.5">User ID</div>
              <div className="font-mono text-xs text-ink-700">{data.id}</div>
            </div>
            <div>
              <div className="eyebrow text-ink-400 mb-0.5">Last login</div>
              <div className="text-ink-700">{data.last_login_at ? formatRelative(data.last_login_at) : <span className="text-ink-400 italic">Never</span>}</div>
            </div>
            <div>
              <div className="eyebrow text-ink-400 mb-0.5">Created</div>
              <div className="text-ink-700">{new Date(data.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</div>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-ink-100">
            <div className="eyebrow text-ink-400 mb-2">Roles ({data.roles?.length ?? 0})</div>
            {!data.roles?.length ? (
              <div className="text-xs text-ink-400 italic">No roles assigned</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.roles.map((r: any) => (
                  <span key={r.code} className="inline-flex items-center px-2 py-0.5 rounded-md bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 text-xs font-medium">
                    {r.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-b border-ink-100">
            <div className="eyebrow text-ink-400 mb-2">Branch access ({data.branches?.length ?? 0})</div>
            {!data.branches?.length ? (
              <div className="text-xs text-ink-400 italic">All branches (unrestricted)</div>
            ) : (
              <div className="space-y-1">
                {data.branches.map((b: any) => (
                  <div key={b.id} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-ink-700 bg-canvas-subtle px-1.5 py-0.5 rounded">{b.code}</span>
                    <span className="text-xs text-ink-600">{b.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-5 py-4 flex gap-2">
            <button className="btn-secondary flex-1 text-sm" onClick={() => onEdit(data)}>Edit</button>
            <button
              className={`flex-1 text-sm font-semibold rounded-xl px-4 py-2 transition-colors ${data.status === 'active' ? 'bg-canvas-subtle text-ink-700 hover:bg-ink-100' : 'bg-sage-50 text-sage-700 hover:bg-sage-100'}`}
              disabled={togglePending}
              onClick={() => onToggle(data)}>
              {data.status === 'active' ? 'Disable' : 'Activate'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────
function Avatar({ name, size = 'md' }: { name: string; size?: 'md'|'lg' }) {
  const initials = name.split(/[\s@]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('');
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-sage-100 text-sage-700','bg-amber-100 text-amber-800','bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-rose-100 text-rose-700','bg-teal-100 text-teal-700'];
  const sz = size === 'lg' ? 'w-12 h-12 text-sm' : 'w-9 h-9 text-xs';
  return <div className={`${sz} rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${tones[hash % tones.length]}`}>{initials || '?'}</div>;
}

// ── Helpers ───────────────────────────────────────────────────
function formatRelative(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (d < 1) return 'Just now';
  if (d < 60) return `${d}m ago`;
  if (d < 1440) return `${Math.floor(d/60)}h ago`;
  if (d < 10080) return `${Math.floor(d/1440)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── UserModal (create + edit) ─────────────────────────────────
function UserModal({ roles, initial, onSubmit, onClose, loading }: {
  roles: any[]; initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    email: initial?.email ?? '',
    fullName: initial?.full_name ?? '',
    password: '',
    roleCodes: (initial?.roles ?? []).map((r: any) => r.code ?? r) as string[],
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Administration</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit user' : 'New user'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Create a new account and assign roles.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Full name</label>
          <input className="input" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} placeholder="Jane Doe" />
        </div>
        {!isEdit && (
          <>
            <div>
              <label className="label">Email address</label>
              <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" />
            </div>
            <div>
              <label className="label">Password</label>
              <input className="input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" />
            </div>
          </>
        )}
        <div>
          <label className="label">Roles</label>
          <div className="border border-ink-200 rounded-xl bg-canvas-elevated max-h-40 overflow-y-auto">
            {roles.length === 0 ? (
              <div className="p-3 text-xs text-ink-500">No roles available</div>
            ) : roles.map((r) => (
              <label key={r.code} className="flex items-center gap-2.5 px-3 py-2 hover:bg-canvas-subtle cursor-pointer border-b border-ink-100 last:border-b-0 transition-colors">
                <input type="checkbox" checked={form.roleCodes.includes(r.code)}
                  onChange={(e) => {
                    const next = e.target.checked ? [...form.roleCodes, r.code] : form.roleCodes.filter((c) => c !== r.code);
                    setForm({ ...form, roleCodes: next });
                  }} className="w-4 h-4 rounded text-sage-500 focus:ring-sage-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-900">{r.name}</div>
                  {r.description && <div className="text-xs text-ink-500 truncate">{r.description}</div>}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary"
          disabled={loading || !form.fullName || (!isEdit && (!form.email || form.password.length < 8))}
          onClick={() => onSubmit(isEdit ? { fullName: form.fullName, roleCodes: form.roleCodes } : form)}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create user')}
        </button>
      </div>
    </Modal>
  );
}

// ── Icons ─────────────────────────────────────────────────────
const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function ImportIcon() { return <svg {...ip}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function UsersIcon() { return <svg {...ip}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>; }
function CheckIcon() { return <svg {...ip}><polyline points="20 6 9 17 4 12"/></svg>; }
function PauseIcon() { return <svg {...ip}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>; }
function LockIcon() { return <svg {...ip}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>; }
